import { t } from '@lingui/macro';
import { useDialog } from 'features/modalManager';
import { SWITCH_COMPANY_DIALOG_ID, useCompanySwitcher } from './useCompanySwitcher';
import { SwitchCompanyModal } from '../components/SwitchCompanyModal';
import { useTheme } from '@emotion/react';

// `onSwitchError` is supplied by the caller (which owns the notification API) so this hook and
// useCompanySwitcher don't import the notifications barrel — see useCompanySwitcher's note.
export const useCompanySwitcherDialog = (onSwitchError?: () => void) => {
  const { openDialog } = useDialog();
  const { palette, s } = useTheme();
  const {
    hasMultiple,
    companies,
    isLoading: isLoadingCompanies,
    isSwitching,
    switchTo,
  } = useCompanySwitcher(onSwitchError);
  const openCompanySwitcherDialog = async () => {
    const result = await openDialog({
      id: SWITCH_COMPANY_DIALOG_ID,
      title: t`Switch company`,
      description: t`Please select the company you want to work with`,
      hideActions: true,
      headerProps: {
        // `&&` doubles the selector specificity so this wins over the Modal's default
        // `padding-bottom: 0` without resorting to `!important` (forbidden — greptile.md).
        sx: {
          '&&': { paddingBottom: s.lg },
          borderBottom: `0.5px solid ${palette?.border?.primarySubtle}`,
        },
      },
      titleProps: { variant: 'headlineMd' },
      modalSx: {
        // Fixed dialog dimensions from the Figma spec (Components (WIP) 10132:2931); the design
        // system has no modal-size token, so these stay as explicit px.
        window: {
          '& .modal-layout-container': {
            width: '587px',
            height: '472px',
          },
        },

        windowBody: { padding: s.lg, '&&&': { paddingTop: s.lg } },
      },
      children: (
        <SwitchCompanyModal
          companies={companies}
          isLoading={isLoadingCompanies}
          isSwitching={isSwitching}
          onSwitch={switchTo}
        />
      ),
    });

    return result;
  };

  return { openCompanySwitcherDialog, hasMultiple };
};
